/**
 * §41.7 — a tracker's postback URL, pasted as the tracker hands it out, turned
 * into one this platform can fill.
 *
 * A tracker gives the affiliate something like
 * `https://t.example/pb?subid=REPLACE&status=dep&payout=REPLACE` and expects
 * each `REPLACE` to become a value. Asking the affiliate to know that `subid`
 * wants our `{sub_id_1}` and `payout` wants `{payout}` was the confusing part;
 * this reads the URL, says which parameters are waiting for a value, suggests
 * the macro each one wants from its name, and writes the URL back with the
 * placeholders swapped for `{macro}`.
 *
 * Browser-safe on purpose — no node builtins — so the affiliate console can
 * reach it through the `@affdice/rules/postback-url-fill` subpath rather than
 * the package index, which pulls in `node:crypto`.
 *
 * Only query values are candidates. A `REPLACE` in the path has no parameter
 * name to say what it wants, and the fragment is never sent.
 *
 * The URL is rewritten by string surgery, never through `new URL().toString()`:
 * WHATWG serialising encodes braces in places, and every byte the affiliate did
 * not ask us to change — a fixed `status=dep`, an escape written in lower case,
 * a `+` — has to reach the tracker exactly as the tracker wrote it.
 */
import { POSTBACK_MACRO_KEYS } from "./postback-macros.js";
export { POSTBACK_MACRO_KEYS } from "./postback-macros.js";
/**
 * "Leave this value as I typed it." Every fillable row offers it, so a value
 * wrongly read as a placeholder never blocks a save.
 */
export const KEEP_AS_TYPED = 'keep';
const MACROS = new Set(POSTBACK_MACRO_KEYS);
/** Never throws. An input with no query, or that is not a URL at all, has no params. */
export function parsePostbackUrl(raw) {
    const params = [];
    const parts = splitUrl(raw.trim());
    if (parts === null)
        return { params };
    let index = 0;
    for (const pair of parts.pairs) {
        if (pair === '')
            continue;
        params.push(readPair(pair, index));
        index += 1;
    }
    return { params };
}
/**
 * The longest postback URL the platform stores, counted on the URL as it is
 * saved: after the placeholders are rewritten to `{macro}`, which is longer
 * than a tracker's `REPLACE`. Registration refuses anything longer, so the
 * form checks the same number before it sends rather than after.
 */
export const POSTBACK_URL_MAX_LENGTH = 2048;
/** True when `url`, trimmed as it is saved, is longer than the platform stores. */
export function postbackUrlTooLong(url) {
    return url.trim().length > POSTBACK_URL_MAX_LENGTH;
}
/**
 * Whether a parameter is named `cid` (in any spelling `suggestPostbackMacro`
 * treats as the same name).
 *
 * `cid` is read as a click ID and filled with `{sub_id_1}`, as the rest of the
 * click-ID family is. Some trackers use it for a campaign ID instead, so the
 * form says which it will carry and how to choose the other. The mapping
 * stays: the click ID is the common use, and the form is where the uncommon
 * one is corrected.
 */
export function isPostbackCidParam(paramName) {
    return normaliseParamName(paramName) === 'cid';
}
/**
 * The macro a parameter name asks for, or null when the name says nothing we
 * can rely on.
 *
 * The name is compared lowercased with `_ - .` and spaces taken out, so
 * `Sub-ID`, `sub_id` and `subid` are one name. A tracker's click ID is what the
 * affiliate put on the tracking link as a sub-ID, so the click-ID family maps
 * to `{sub_id_1}`, not to our own `{click_id}`, which the tracker has never
 * seen. A numbered form (`s3`, `sub3`, `subid3`, `aff_sub3`) picks that slot,
 * and the same names without a number mean the first; there is no slot above 10.
 */
export function suggestPostbackMacro(paramName) {
    const n = normaliseParamName(paramName);
    const numbered = /^(?:s|sub|subid|affsub)([1-9]|10)?$/.exec(n);
    if (numbered)
        return `sub_id_${numbered[1] ?? '1'}`;
    return BY_NAME.get(n) ?? null;
}
/**
 * Whether a query value is waiting to be filled: a tracker placeholder, one of
 * our own `{macro}`s, or empty.
 *
 * Conservative on purpose — a value read as a placeholder is offered for
 * rewriting, and a fixed value must never be. Recognised: `REPLACE`,
 * `REPLACE_ME`, `REPLACEME` (any case); `{x}`, `{{x}}` and `[x]` around an
 * identifier, raw or percent-encoded; `%x%` with x starting with a letter, so
 * an escape like `%20` or `%7B` never reads as one. Not recognised: `XXX`,
 * which is also the ISO 4217 code for "no currency"; bare words; numbers;
 * `<x>`, `$x` and `#x#`, which are too often real values.
 */
export function isPostbackPlaceholder(rawValue) {
    return rawValue === '' || classify(rawValue) !== null;
}
/**
 * The choice each fillable row starts on: an existing macro keeps itself, a
 * placeholder gets its suggestion (null when there is none, so the affiliate
 * has to pick), and an empty value gets its suggestion or else is left as
 * typed — `name=` may be exactly what the tracker wants.
 *
 * Opening a saved registration to edit it (`editing`) starts from what is
 * stored instead. That URL was saved as it is, and a value the affiliate
 * chose to leave then — a `sub1=`, an `x={nope}` — is not something to fill
 * or to block on now: an affiliate who opens Edit only to rotate the secret
 * would otherwise send a URL they never touched, or be unable to save at
 * all. Only a stored placeholder with a suggestion still starts on it, so a
 * `REPLACE` saved before autofill existed is offered its fix.
 */
export function initialPostbackFills(parsed, mode = {}) {
    const choices = {};
    for (const p of parsed.params) {
        if (p.fill === null)
            continue;
        const { source, suggested } = p.fill;
        if (mode.editing) {
            choices[p.index] = source === 'empty' ? KEEP_AS_TYPED : suggested ?? KEEP_AS_TYPED;
        }
        else {
            choices[p.index] = source === 'empty' ? suggested ?? KEEP_AS_TYPED : suggested;
        }
    }
    return choices;
}
/**
 * Our macros inside a longer fixed value, as the sender will fill them:
 * `ref=aff_{sub_id_1}` goes out as `ref=aff_<the sub-ID>`, though the row is
 * not a placeholder and is never rewritten. Each named once, in order; empty
 * for a value that is sent exactly as typed.
 */
export function postbackMacrosInValue(rawValue) {
    const found = new Set();
    for (const m of rawValue.matchAll(FILLED)) {
        const key = m[1] ?? m[2];
        if (MACROS.has(key))
            found.add(key);
    }
    return [...found];
}
/** True when every fillable row has a choice — a macro or leave-as-typed. */
export function postbackFillsComplete(parsed, choices) {
    return parsed.params.every((p) => p.fill === null || isChoice(choices[p.index]));
}
/**
 * The URL with each chosen placeholder replaced by `{macro}`.
 *
 * Only fillable rows whose choice is a macro are rewritten, and a row that
 * already held one of our macros only when the choice differs: an unchanged
 * legacy `%7Bclick_id%7D` goes back as stored, not re-spelled. That is safe
 * because a row reads as a macro only in a spelling the sender fills; a
 * `%7Bpayout}` is a placeholder, and is re-spelled `{payout}`. Everything else
 * — fixed values, the path, empty pairs, the fragment, leave-as-typed rows —
 * is copied byte for byte. With no choices the result is `raw.trim()`.
 */
export function applyPostbackFills(raw, choices) {
    const trimmed = raw.trim();
    const parts = splitUrl(trimmed);
    if (parts === null)
        return trimmed;
    let index = 0;
    const pairs = parts.pairs.map((pair) => {
        if (pair === '')
            return pair;
        const param = readPair(pair, index);
        index += 1;
        const choice = choices[param.index];
        if (param.fill === null || !isMacro(choice))
            return pair;
        if (param.fill.source === 'macro' && choice === param.fill.suggested)
            return pair;
        return `${param.name}={${choice}}`;
    });
    return `${parts.head}?${pairs.join('&')}${parts.fragment}`;
}
// ---------------------------------------------------------------------------
/** Normalised parameter name → macro. Numbered sub-ID names are handled apart. */
const BY_NAME = new Map([
    ['sub_id_1', ['subid', 'sid', 'clickid', 'click', 'clid', 'clkid', 'cid', 'affsub', 'affclickid', 'externalid', 'extid']],
    ['payout', ['payout', 'payoutamount', 'sum', 'amount', 'revenue', 'commission', 'comm', 'profit', 'value', 'price', 'earnings', 'reward', 'payment']],
    ['deposit_amount', ['depositamount', 'deposit', 'dep', 'depositsum', 'depositvalue', 'depamount', 'depsum', 'ftdamount']],
    ['withdrawal_amount', ['withdrawalamount', 'withdrawal', 'withdrawalsum', 'wdamount', 'wdsum']],
    ['ngr', ['ngr', 'netrevenue', 'netgamingrevenue', 'ngramount']],
    ['period_start', ['periodstart', 'periodfrom', 'startdate']],
    ['period_end', ['periodend', 'periodto', 'enddate']],
    ['brand_url', ['brandurl', 'brandsite', 'siteurl', 'websiteurl', 'webappurl', 'appurl']],
    ['currency', ['currency', 'cur', 'curr', 'ccy']],
    ['country', ['country', 'geo', 'cc', 'countrycode', 'iso']],
    ['status', ['status', 'state', 'conversionstatus', 'convstatus']],
    ['event', ['event', 'eventtype', 'eventname', 'action', 'type', 'et']],
    ['goal', ['goal', 'goalid', 'goalname', 'goalkey']],
    ['id', ['id', 'txid', 'transactionid', 'transid', 'conversionid', 'convid', 'orderid', 'leadid', 'actionid', 'uniqueid']],
    ['player_ref', ['playerid', 'player', 'playerref', 'userid', 'uid', 'user', 'customerid']],
    ['partner_id', ['partnerid', 'affiliateid', 'affid', 'affiliate', 'publisherid', 'pubid']],
    ['offer_id', ['offerid', 'offer', 'campaignid', 'campaign', 'campid', 'cmp']],
    ['advertiser_id', ['advertiserid', 'advid', 'brandid', 'brand', 'merchantid']],
    ['conversion_date', ['date', 'time', 'timestamp', 'datetime', 'ts', 'conversiondate', 'conversiontime']],
    // No `tid` row: our `{tid}` is empty on most conversions (a goal line
    // the worker writes has none), and trackers use a `tid` of their own
    // for fixed values, so the name is no evidence of what it wants.
].flatMap(([macro, names]) => names.map((name) => [name, macro])));
/**
 * Null when there is no query. A `?` after a `#` is inside the fragment
 * (`/pb#x?y=REPLACE`), which is never sent, so that URL has no query either.
 */
function splitUrl(s) {
    const q = s.indexOf('?');
    if (q === -1)
        return null;
    const hash = s.indexOf('#');
    if (hash !== -1 && hash < q)
        return null;
    const end = hash === -1 ? s.length : hash;
    return {
        head: s.slice(0, q),
        pairs: s.slice(q + 1, end).split('&'),
        fragment: s.slice(end),
    };
}
function readPair(pair, index) {
    const eq = pair.indexOf('=');
    // A bare `flag` with no `=` is fixed: filling it would add an `=` the
    // tracker never asked for.
    if (eq === -1)
        return { index, name: pair, raw: '', fill: null };
    const name = pair.slice(0, eq);
    const raw = pair.slice(eq + 1);
    if (raw === '')
        return { index, name, raw, fill: { source: 'empty', suggested: suggestPostbackMacro(name) } };
    const found = classify(raw);
    if (found === null)
        return { index, name, raw, fill: null };
    if (found.source === 'macro')
        return { index, name, raw, fill: { source: 'macro', suggested: found.token } };
    // The token inside the placeholder says more than the name around it:
    // `ref={clickid}` is a click ID although `ref` says nothing.
    const suggested = (found.token === null ? null : suggestPostbackMacro(found.token)) ?? suggestPostbackMacro(name);
    return { index, name, raw, fill: { source: 'placeholder', suggested } };
}
/**
 * The two spellings `fillPostbackMacros` fills, and the only two: `{key}`, and
 * `%7Bkey%7D` with both braces encoded (any hex case). Kept in step with the
 * pattern there.
 */
const FILLED = /\{(\w+)\}|%7b(\w+)%7d/gi;
const FILLED_WHOLE = new RegExp(`^(?:${FILLED.source})$`, 'i');
const REPLACE_WORD = /^replace(?:_?me)?$/i;
const BRACED = /^(?:\{([A-Za-z_]\w*)\}|\{\{([A-Za-z_]\w*)\}\}|\[([A-Za-z_]\w*)\])$/;
const PERCENT_WRAPPED = /^%([A-Za-z]\w*)%$/;
/**
 * A value is our macro only as written, in a spelling the sender fills.
 * Anything else is read both as written and percent-decoded, so `%7Bx%7D`
 * and `%5Bx%5D` read as `{x}` and `[x]`. A `%x%` placeholder is not valid
 * percent-encoding and is matched as written.
 */
function classify(raw) {
    // Only `{key}` or `%7Bkey%7D` with our exact key is ours — the only forms
    // `fillPostbackMacros` fills, so the only ones safe to write back as they
    // are. `{{key}}` would come out still wrapped in braces, `{Key}` would not
    // be filled at all, and neither would `%7Bkey}` or `%7B%6Bey%7D`, which
    // only decode to `{key}`: each of those is a placeholder, rewritten on save.
    const own = FILLED_WHOLE.exec(raw);
    const ownKey = own ? own[1] ?? own[2] : null;
    if (ownKey !== null && MACROS.has(ownKey))
        return { source: 'macro', token: ownKey };
    for (const v of new Set([raw, safeDecode(raw)])) {
        if (REPLACE_WORD.test(v))
            return { source: 'placeholder', token: null };
        const braced = BRACED.exec(v);
        if (braced) {
            const [, single, double, square] = braced;
            return { source: 'placeholder', token: single ?? double ?? square };
        }
        const wrapped = PERCENT_WRAPPED.exec(v);
        if (wrapped)
            return { source: 'placeholder', token: wrapped[1] };
    }
    return null;
}
/** Lowercased, decoded, with `_ - .` and spaces taken out: `Sub-ID` is `subid`. */
function normaliseParamName(paramName) {
    return safeDecode(paramName).toLowerCase().replace(/[_\-.\s]/g, '');
}
function safeDecode(s) {
    try {
        return decodeURIComponent(s);
    }
    catch {
        return s;
    }
}
function isMacro(choice) {
    return typeof choice === 'string' && MACROS.has(choice);
}
function isChoice(choice) {
    return choice === KEEP_AS_TYPED || isMacro(choice);
}
//# sourceMappingURL=postback-url-fill.js.map